import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TeamProvider } from './context/TeamContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { AssistantProvider } from './context/AssistantContext';
import { Assistant } from './components/ChatPanel';
import { OfflineGate } from './components/OfflineScreen';
import { setDeepLinkHandler } from './native/push';
import { cleanupFirebaseRedirectState, isFirebaseConfigured } from './config/firebase';
import LoginPage from './pages/LoginPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import AuthActionPage from './pages/AuthActionPage';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import LineupConfirmationPage from './pages/LineupConfirmationPage';
import UnsubscribePage from './pages/UnsubscribePage';
import Dashboard from './pages/Dashboard';
import MatchesPage from './pages/MatchesPage';
import MatchDetailPage from './pages/MatchDetailPage';
import MatchScorecardPage from './pages/MatchScorecardPage';
import TeamPage from './pages/TeamPage';
import ProfilePage from './pages/ProfilePage';
import AvailabilityPage from './pages/AvailabilityPage';
import DirectoryPage from './pages/DirectoryPage';
import LegalPage from './pages/landing/LegalPage';
import LoadingSpinner from './components/LoadingSpinner';
import PostHogIdentity from './components/PostHogIdentity';
import RebrandBanner from './components/RebrandBanner';
import { useFeatureFlag } from './utils/useFeatureFlag';
import { IS_IOS_APP } from './config/platform';
import { BRAND } from './brand';
import tennisBallSvg from './assets/tennis_ball.svg';

/**
 * Captain, admin and billing pages — `null` in the iOS build.
 *
 * These are `require`d inside a branch on the raw env var rather than `import`ed
 * at the top because that is the only form webpack can actually prune: it
 * substitutes REACT_APP_PLATFORM via DefinePlugin, folds the test to a literal,
 * and drops the dead branch along with everything it pulls in. A top-level
 * `import` is hoisted and unconditional, so TeamManagementPage (~13k LOC) and
 * the Stripe-backed SubscriptionPage would ship inside the .ipa even though no
 * route renders them — and App Store guideline 3.1.1 is about what is in the
 * binary, not about what the router happens to reach.
 *
 * The condition is spelled out here instead of reusing IS_IOS_APP because
 * webpack folds `process.env.*` in the module it parses; an imported constant
 * is opaque to it. Everything downstream still branches on `WebOnlyPages`.
 */
const WebOnlyPages: {
  TeamManagementPage: React.ComponentType;
  TeamCreationWizard: React.ComponentType;
  AdminPanelPage: React.ComponentType;
  SubscriptionPage: React.ComponentType;
  LandingPage: React.ComponentType;
  PricingPage: React.ComponentType;
} | null =
  process.env.REACT_APP_PLATFORM === 'ios'
    ? null
    : {
        TeamManagementPage: require('./pages/TeamManagementPage').default,
        TeamCreationWizard: require('./pages/TeamCreationWizard').default,
        AdminPanelPage: require('./pages/AdminPanelPage').default,
        SubscriptionPage: require('./pages/SubscriptionPage').default,
        // The marketing page is web-only for the same reason: its hero carries
        // "Free for a limited time · No credit card needed", which is pricing copy
        // for a purchase the iOS build deliberately cannot offer.
        LandingPage: require('./pages/landing/LandingPage').default,
        // A pricing page is the most literal form of the thing guideline 3.1.1
        // forbids: it advertises a subscription that cannot be bought in-app.
        PricingPage: require('./pages/landing/PricingPage').default,
      };

// Base routes that survive a relaunch. The captain/admin/billing paths do not
// exist in the iOS build, so a `lastRoute` left over from the web app must not
// be restored there — it would resolve to nothing and render a blank screen.
const RESTORABLE_ROUTES = [
  '/dashboard',
  '/availability',
  '/matches',
  '/profile',
  '/directory',
  ...(IS_IOS_APP ? [] : ['/teams', '/admin', '/subscription']),
];

/** Exported for tests — see RESTORABLE_ROUTES. */
export const isRestorableRoute = (pathname: string): boolean =>
  RESTORABLE_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'));

/**
 * Whether this pathname is worth remembering as `lastRoute`. Only for
 * authenticated users, and never the login or root route.
 */
export const shouldPersistRoute = (pathname: string, isAuthenticated: boolean): boolean =>
  isAuthenticated && pathname !== '/login' && pathname !== '/' && isRestorableRoute(pathname);

/** Where to land after auth: the stored route when this build still has it. */
export const getStoredRoute = (): string => {
  const lastRoute = localStorage.getItem('lastRoute');
  return lastRoute && isRestorableRoute(lastRoute) ? lastRoute : '/dashboard';
};

// Component to track and persist route changes
const RoutePersistence: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (shouldPersistRoute(location.pathname, !!user)) {
      // Store the full pathname for exact restoration (including dynamic segments)
      localStorage.setItem('lastRoute', location.pathname);
    }
  }, [location.pathname, user]);

  return null;
};

// Routes notification taps to the screen the payload points at.
// Registering the handler also drains any link buffered before the router existed,
// which is what makes a tap that cold-starts the app land on the right match instead
// of the dashboard. No-ops on web.
const PushDeepLinks: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    setDeepLinkHandler((link) => navigate(link));
    return () => setDeepLinkHandler(null);
  }, [navigate]);

  return null;
};

/** Exported for tests — the iOS capacity short-circuit is the part that matters. */
export const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: 'ADMIN' | 'PLAYER' }> = ({
  children, 
  requiredRole 
}) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const subscriptionsEnabled = useFeatureFlag('enable-subscriptions', false);
  // Start settled on iOS rather than starting on <LoadingSpinner/>: the capacity
  // check blocked EVERY protected route mount behind a GET /users/me/profile,
  // which on cellular reads as a spinner flash on every single tap.
  const [isCheckingCapacity, setIsCheckingCapacity] = React.useState(!IS_IOS_APP);
  const [isOverCapacity, setIsOverCapacity] = React.useState(false);

  // Check subscription capacity
  React.useEffect(() => {
    const checkCapacity = async () => {
      // See the note on isCheckingCapacity — it already starts false here.
      if (IS_IOS_APP) {
        return;
      }

      if (!user || isLoading) {
        setIsCheckingCapacity(false);
        return;
      }

      // If subscriptions are disabled via feature flag, bypass all checks
      if (!subscriptionsEnabled) {
        setIsCheckingCapacity(false);
        return;
      }

      // Admins are exempt from capacity checks
      if (user.role === 'ADMIN') {
        setIsCheckingCapacity(false);
        return;
      }

      // Allow access to subscription page even if over capacity
      if (location.pathname === '/subscription') {
        setIsCheckingCapacity(false);
        return;
      }

      try {
        const response = await axios.get('/users/me/profile');
        const subscriptionInfo = response.data.subscription_info;
        
        if (subscriptionInfo) {
          const { active_teams_count, max_active_teams, is_active } = subscriptionInfo;
          // Block if:
          // 1. Over capacity (active_teams_count > max_active_teams)
          // 2. No subscription (max_active_teams = 0) but has active teams
          // 3. Subscription is inactive/expired (is_active = false) but has active teams
          if (active_teams_count > max_active_teams || 
              (max_active_teams === 0 && active_teams_count > 0) ||
              (!is_active && active_teams_count > 0)) {
            setIsOverCapacity(true);
          }
        }
      } catch (error) {
        // If we can't check capacity, allow access (fail open)
        console.error('Error checking subscription capacity:', error);
      } finally {
        setIsCheckingCapacity(false);
      }
    };

    checkCapacity();
  }, [user, isLoading, location.pathname, subscriptionsEnabled]);

  if (isLoading || isCheckingCapacity) {
    return <LoadingSpinner />;
  }

  if (!user) {
    const originalPath = location.pathname + location.search;
    return <Navigate to="/login" replace state={{ returnTo: originalPath }} />;
  }

  // Redirect to subscription page if over capacity (except if already on subscription page or subscriptions disabled).
  // !IS_IOS_APP leads deliberately: as the first operand it is a literal false
  // in the iOS build, so the whole branch — the '/subscription' string included
  // — is folded out of the bundle rather than merely left unreachable.
  if (!IS_IOS_APP && isOverCapacity && location.pathname !== '/subscription' && subscriptionsEnabled) {
    return <Navigate to="/subscription" replace />;
  }

  if (requiredRole && user.role !== requiredRole && user.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Component to handle root route redirection with persistence
const RootRedirect: React.FC = () => {
  const { user } = useAuth();

  return <Navigate to={user ? getStoredRoute() : '/dashboard'} replace />;
};

// Component to handle /teams redirect to default tab
const TeamsRedirect: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  // Get stored tab from localStorage, or default to 'members'
  // Map internal tab names to URL tab names (teamSchedule -> schedule)
  const getStoredTab = (): string => {
    const stored = localStorage.getItem('teamManagementActiveTab');
    // Backward compatibility: migrate old keys to new ones
    if (stored === 'scheduling') return 'strategize';
    if (stored === 'matches' || stored === 'teamSchedule') return 'schedule';
    if (stored && ['members', 'teamSchedule', 'availability', 'strategize', 'configuration'].includes(stored)) {
      // Map teamSchedule to schedule for URL
      return stored === 'teamSchedule' ? 'schedule' : stored;
    }
    return 'members';
  };
  
  const defaultTab = getStoredTab();
  const queryString = searchParams.toString();
  const redirectPath = `/teams/${defaultTab}${queryString ? `?${queryString}` : ''}`;
  return <Navigate to={redirectPath} replace />;
};

// Component to handle login route redirect when user is already authenticated
const LoginRedirect: React.FC = () => {
  const location = useLocation();

  // Check if there's a returnTo in location state (from ProtectedRoute redirect)
  const returnTo = (location.state as { returnTo?: string })?.returnTo;
  const redirectTo = returnTo || getStoredRoute();

  return <Navigate to={redirectTo} replace />;
};

/**
 * The whole route table as a flat array, so a test can read it back without
 * standing up the provider tree. `/subscription`, `/teams*` and `/admin` must
 * stay out of it in the iOS build: that assertion is the tripwire keeping a
 * Stripe checkout from reappearing in front of App Review.
 */
export const buildRoutes = (isAuthenticated: boolean): React.ReactElement[] => {
  const routes: React.ReactElement[] = [
    <Route
      key="/login"
      path="/login"
      element={isAuthenticated ? <LoginRedirect /> : <LoginPage />}
    />,
    <Route key="/auth-action" path="/auth-action" element={<AuthActionPage />} />,
    <Route key="/verify-email" path="/verify-email" element={<EmailVerificationPage />} />,
    <Route
      key="/invitations/accept/:token"
      path="/invitations/accept/:token"
      element={<InvitationAcceptPage />}
    />,
    <Route
      key="/lineup/confirm/:token"
      path="/lineup/confirm/:token"
      element={<LineupConfirmationPage />}
    />,
    <Route key="/unsubscribe" path="/unsubscribe" element={<UnsubscribePage />} />,
    <Route key="/privacy" path="/privacy" element={<LegalPage kind="privacy" />} />,
    <Route key="/terms" path="/terms" element={<LegalPage kind="terms" />} />,
    // Web only — see WebOnlyPages. Privacy and terms stay in the iOS build
    // because App Review requires reaching them from inside the app; pricing is
    // the opposite, and must not ship at all.
    ...(WebOnlyPages
      ? [
          <Route
            key="/pricing"
            path="/pricing"
            element={<WebOnlyPages.PricingPage />}
          />,
        ]
      : []),
    <Route
      key="/dashboard"
      path="/dashboard"
      element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/dashboard/team/:teamId"
      path="/dashboard/team/:teamId"
      element={
        <ProtectedRoute>
          <TeamPage />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/matches"
      path="/matches"
      element={
        <ProtectedRoute>
          <MatchesPage />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/matches/:matchId"
      path="/matches/:matchId"
      element={
        <ProtectedRoute>
          <MatchDetailPage />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/matches/:matchId/scorecard"
      path="/matches/:matchId/scorecard"
      element={
        <ProtectedRoute>
          <MatchScorecardPage />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/availability"
      path="/availability"
      element={
        <ProtectedRoute>
          <AvailabilityPage />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/profile"
      path="/profile"
      element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      }
    />,
    <Route
      key="/directory"
      path="/directory"
      element={
        <ProtectedRoute>
          <DirectoryPage />
        </ProtectedRoute>
      }
    />,
  ];

  // Captain, admin and billing surfaces: web only. See WebOnlyPages above —
  // when this is null the pages are not merely unrouted, they are not in the
  // bundle at all.
  if (WebOnlyPages) {
    routes.push(
      <Route
        key="/teams/create-wizard"
        path="/teams/create-wizard"
        element={
          <ProtectedRoute>
            <WebOnlyPages.TeamCreationWizard />
          </ProtectedRoute>
        }
      />,
      <Route
        key="/teams/:tab"
        path="/teams/:tab"
        element={
          <ProtectedRoute>
            <WebOnlyPages.TeamManagementPage />
          </ProtectedRoute>
        }
      />,
      <Route
        key="/teams"
        path="/teams"
        element={
          <ProtectedRoute>
            <TeamsRedirect />
          </ProtectedRoute>
        }
      />,
      <Route
        key="/admin"
        path="/admin"
        element={
          <ProtectedRoute requiredRole="ADMIN">
            <WebOnlyPages.AdminPanelPage />
          </ProtectedRoute>
        }
      />,
      <Route
        key="/subscription"
        path="/subscription"
        element={
          <ProtectedRoute>
            <WebOnlyPages.SubscriptionPage />
          </ProtectedRoute>
        }
      />,
    );
  }

  // Public marketing landing at the root; signed-in users go to their app.
  // In the iOS app there is no marketing funnel to run — the user already installed it —
  // so signed-out launches go straight to login. This also removes two App Store
  // liabilities: a native app that opens on a web marketing page reads as a repackaged
  // website (guideline 4.2), and the landing hero carries "Free for a limited time ·
  // No credit card needed", which is pricing copy for a purchase that cannot happen
  // in-app (guideline 3.1.1).
  routes.push(
    <Route
      key="/"
      path="/"
      element={
        isAuthenticated ? (
          <RootRedirect />
        ) : WebOnlyPages ? (
          <WebOnlyPages.LandingPage />
        ) : (
          <Navigate to="/login" replace />
        )
      }
    />,
  );

  return routes;
};

const AppRoutes: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <RoutePersistence />
      <PushDeepLinks />
      <Routes>{buildRoutes(!!user)}</Routes>
    </>
  );
};

// Reusable gradient background component
const GradientBackground: React.FC = () => {
  const [positions, setPositions] = React.useState<Array<{ top: number; left: number; rotation: number }>>([]);

  React.useEffect(() => {
    // Generate random positions that don't overlap
    const generateNonOverlappingPositions = () => {
      // Only 1 blob
      const numBlobs = 1;
      const newPositions: Array<{ top: number; left: number; rotation: number }> = [];
      const minDistancePercent = 25; // Minimum 25% distance between blob centers
      const padding = 5; // Keep blobs away from edges (5% padding - allows it to go off screen a bit)
      const maxAttempts = 100;
      
      for (let i = 0; i < numBlobs; i++) {
        let attempts = 0;
        let position: { top: number; left: number; rotation: number } | null = null;
        let isValid = false;
        
        while (!isValid && attempts < maxAttempts) {
          // Generate random position within safe area (padding to 100-padding)
          // Generate random rotation (0 to 360 degrees)
          const candidatePosition = {
            top: Math.random() * (100 - padding * 2) + padding,
            left: Math.random() * (100 - padding * 2) + padding,
            rotation: Math.random() * 360,
          };
          
          // Check if this position overlaps with existing positions
          isValid = newPositions.every(existingPos => {
            const distanceX = Math.abs(candidatePosition.left - existingPos.left);
            const distanceY = Math.abs(candidatePosition.top - existingPos.top);
            const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
            return distance >= minDistancePercent;
          });
          
          if (isValid) {
            position = candidatePosition;
          }
          
          attempts++;
        }
        
        if (isValid && position) {
          newPositions.push(position);
        } else {
          // Fallback: use default positions if we can't find non-overlapping ones
          const fallbackPositions = [
            { top: 30, left: 30, rotation: Math.random() * 360 },
            { top: 70, left: 70, rotation: Math.random() * 360 },
            { top: 50, left: 50, rotation: Math.random() * 360 },
          ];
          newPositions.push(fallbackPositions[i] || fallbackPositions[0]);
        }
      }
      
      return newPositions;
    };
    
    // Generate positions on mount
    const newPositions = generateNonOverlappingPositions();
    setPositions(newPositions);
    
    // Regenerate on window resize (debounced)
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const updatedPositions = generateNonOverlappingPositions();
        setPositions(updatedPositions);
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <>
      <div 
        className="fixed bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 -z-10"
        style={{
          top: 'calc(-1 * env(safe-area-inset-top, 0px))',
          left: 'calc(-1 * env(safe-area-inset-left, 0px))',
          right: 'calc(-1 * env(safe-area-inset-right, 0px))',
          bottom: 'calc(-1 * env(safe-area-inset-bottom, 0px))',
          width: 'calc(100% + env(safe-area-inset-left, 0px) + env(safe-area-inset-right, 0px))',
          height: 'calc(100% + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px))',
        }}
      ></div>
      {/* Background decoration - Hazy tennis ball effect */}
      <div 
        className="fixed overflow-hidden pointer-events-none -z-10"
        style={{
          top: 'calc(-1 * env(safe-area-inset-top, 0px))',
          left: 'calc(-1 * env(safe-area-inset-left, 0px))',
          right: 'calc(-1 * env(safe-area-inset-right, 0px))',
          bottom: 'calc(-1 * env(safe-area-inset-bottom, 0px))',
          width: 'calc(100% + env(safe-area-inset-left, 0px) + env(safe-area-inset-right, 0px))',
          height: 'calc(100% + env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {positions.map((position, index) => (
          <div 
            key={index}
            className="absolute"
            style={{
              top: `calc(${position.top}% - 160px)`,
              left: `calc(${position.left}% - 160px)`,
            }}
          >
            <div 
              className="w-80 h-80 mix-blend-multiply filter blur-lg opacity-25 dark:opacity-10 animate-blob animate-spin-slow"
              style={{
                backgroundImage: `url(${tennisBallSvg})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            ></div>
          </div>
        ))}
      </div>
    </>
  );
};

const App: React.FC = () => {
  // Clean up any stale Firebase redirect state on app initialization
  // This prevents "missing initial state" errors when users come from email links
  useEffect(() => {
    if (isFirebaseConfigured()) {
      cleanupFirebaseRedirectState();
    }
    // Keep the tab title in sync with the active brand (the static title in
    // public/index.html is brand-specific via the apply-brand build step, but
    // this also covers SPA navigations and any runtime brand changes).
    document.title = BRAND.name;
  }, []);
  return (
    <AuthProvider>
      <ThemeProvider>
        <TeamProvider>
          <ToastProvider>
            <Router>
              <PostHogIdentity />
              <GradientBackground />
              <AssistantProvider>
                <div
                  className="min-h-screen relative z-0 pb-4 md:pb-0"
                  style={{
                    paddingTop: 'env(safe-area-inset-top, 0px)',
                    paddingLeft: 'env(safe-area-inset-left, 0px)',
                    paddingRight: 'env(safe-area-inset-right, 0px)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  }}
                >
                  <RebrandBanner />
                  <OfflineGate>
                    <AppRoutes />
                  </OfflineGate>
                </div>
                <Assistant />
              </AssistantProvider>
            </Router>
          </ToastProvider>
        </TeamProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;